import { Dayjs } from 'dayjs'
import orderBy from 'lodash/orderBy'

import { day, getCalendarDates, getOnlyDate } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { useStore } from '~/store/store'
import { getTimeOfDayOrder } from '~/utils/surgeryActivities'

import {
    Include,
    PractitionerScheduleLocation,
    PractitionerScheduleStatus,
    useLocationScheduleSpecialities,
    usePractitioners,
    usePractitionerScheduleLocations,
    usePractitionerScheduleStatuses,
} from '../'
import { isPractitionerActive } from './practitioners'
import { SCHEDULE_PRACTITIONER_INCLUDE, SchedulePractitioner, useScheduledPractitionersWithDepartmentsForLocationDay } from './practitionerScheduleLocations'

const SCHEDULE_PRACTITIONER_FILTER_INCLUDE = { ...SCHEDULE_PRACTITIONER_INCLUDE, servicePeriods: true } as const

const SCHEDULE_LOCATION_INCLUDE = { location: true } as const

export type ScheduleLocation = Include<PractitionerScheduleLocation, typeof SCHEDULE_LOCATION_INCLUDE>

export const PRACTITIONER_SCHEDULE_STATUS_CELL_INCLUDE = { definition: true } as const
export type PractitionerScheduleStatusCell = Include<PractitionerScheduleStatus, typeof PRACTITIONER_SCHEDULE_STATUS_CELL_INCLUDE>

// Post-query sort: Storm orderBy comparators are typed on scalar rows, not included relations.
function sortCellScheduleStatuses(statuses: PractitionerScheduleStatusCell[]): PractitionerScheduleStatusCell[] {
    return orderBy(statuses, [s => getTimeOfDayOrder(s.definition?.time_of_day), s => (s.isOptimistic ? 0 : 1), s => s.updated_at])
}

export type ScheduleStatus = PractitionerScheduleStatusCell

export type DaySchedule = {
    date: Dayjs
    practitioner: SchedulePractitioner
    scheduleLocations: ScheduleLocation[]
    scheduleStatuses: ScheduleStatus[]
}

export const OPERATING_SCHEDULE_STATUS_CODES = ['SURGERY', 'DAY_SURGERY'] as const

const SURGERY_SCHEDULE_STATUS_CODES = new Set<string>(OPERATING_SCHEDULE_STATUS_CODES)

export function hasOperatingScheduleStatusCode(statusCodes: readonly string[]): boolean {
    return statusCodes.some(code => SURGERY_SCHEDULE_STATUS_CODES.has(code))
}

export function hasSurgeryScheduleStatus(schedule: Pick<DaySchedule, 'scheduleStatuses'>): boolean {
    return schedule.scheduleStatuses.some(s => SURGERY_SCHEDULE_STATUS_CODES.has(s.status_code))
}

export function usePractitionerScheduleStatusesForCell(practitionerId: number, scheduledDate: string): PractitionerScheduleStatusCell[] {
    const statuses = usePractitionerScheduleStatuses({
        where: { practitioner_id: practitionerId, scheduled_date: scheduledDate },
        include: PRACTITIONER_SCHEDULE_STATUS_CELL_INCLUDE,
    })
    return sortCellScheduleStatuses(statuses)
}

function isAssignedSurgeryInThisLocation(schedule: DaySchedule, locationId: number): boolean {
    return hasSurgeryScheduleStatus(schedule) && schedule.scheduleLocations.every(loc => loc.location_id === locationId)
}

/** Mirrors legacy `selectGetSchedules().byDatePractitionerId` — always returns a row for known practitioners. */
function resolveDaySchedule(date: Dayjs, practitioner: SchedulePractitioner, schedules: DaySchedule[]): DaySchedule {
    const existing = schedules.find(s => s.practitioner.id === practitioner.id)
    if (existing) return existing
    return {
        date: day(date),
        practitioner,
        scheduleLocations: [],
        scheduleStatuses: [],
    }
}

function usePractitionersActiveInSelectedMonth() {
    const selectedDate = useStore(state => state.appFilters.selectedDate)
    const daysOfMonth = getCalendarDates(selectedDate)
    const monthStart = daysOfMonth.at(0)
    const monthEnd = daysOfMonth.at(-1)
    const practitioners = usePractitioners({ include: SCHEDULE_PRACTITIONER_FILTER_INCLUDE })

    if (!monthStart || !monthEnd) return practitioners
    return practitioners.filter(p => isPractitionerActive(p, monthStart, monthEnd))
}

export function usePractitionerSchedulesForDate(date: Dayjs): DaySchedule[] {
    const dateKey = getOnlyDate(date)
    const activePractitionerIds = new Set(usePractitionersActiveInSelectedMonth().map(p => p.id))

    const statusesByPractitionerId = usePractitionerScheduleStatuses({
        where: { scheduled_date: dateKey },
        include: PRACTITIONER_SCHEDULE_STATUS_CELL_INCLUDE,
        groupBy: 'practitioner_id',
    })
    const locationsByPractitionerId = usePractitionerScheduleLocations({
        where: { scheduled_date: dateKey },
        include: SCHEDULE_LOCATION_INCLUDE,
        groupBy: 'practitioner_id',
    })

    const practitionerIds = [...new Set([...statusesByPractitionerId.keys(), ...locationsByPractitionerId.keys()])].filter(id => activePractitionerIds.has(id))

    const practitioners = usePractitioners({
        where: practitionerIds.length > 0 ? { id: { in: practitionerIds } } : { id: -1 },
        include: SCHEDULE_PRACTITIONER_INCLUDE,
    })

    const practitionerById = new Map(practitioners.map(p => [p.id, p]))

    return practitionerIds
        .map(id => {
            const practitioner = practitionerById.get(id)
            if (!practitioner) return undefined
            return {
                date: day(date),
                practitioner,
                scheduleLocations: locationsByPractitionerId.get(id) ?? [],
                scheduleStatuses: statusesByPractitionerId.get(id) ?? [],
            }
        })
        .filter((s): s is DaySchedule => s !== undefined)
}

type SuggestedPractitionersBrowseResult = {
    mode: 'browse'
    availableSchedulesSelectedSpecialties: DaySchedule[]
    availableSchedulesOtherSpecialties: DaySchedule[]
    schedulesAtOtherLocations: DaySchedule[]
    otherPractitionerSchedules: DaySchedule[]
    selectedPractitioners: SchedulePractitioner[]
}

type SuggestedPractitionersSearchResult = {
    mode: 'search'
    schedules: DaySchedule[]
    selectedPractitioners: SchedulePractitioner[]
}

export function useSuggestedPractitionersForLocation(
    date: Dayjs,
    locationId: number,
    search: string
): SuggestedPractitionersBrowseResult | SuggestedPractitionersSearchResult {
    const isSpecialtiesSelectionEnabled = useFlag('use_specialties_selection')

    const locationScheduleSpecialities = useLocationScheduleSpecialities({ where: { scheduled_date: getOnlyDate(date), location_id: locationId } })
    const allocatedSpecialityUuids = new Set(locationScheduleSpecialities.map(lss => lss.speciality_uuid))
    const departmentKey = useStore(state => state.appFilters.departmentKey)

    const schedules = usePractitionerSchedulesForDate(date)
    const allPractitioners = usePractitionersActiveInSelectedMonth()
    const selectedPractitioners = useScheduledPractitionersWithDepartmentsForLocationDay(date, locationId)

    if (search.length > 0) {
        const searchLower = search.trim().toLowerCase()
        const matchingPractitioners = allPractitioners.filter(p => p.short_name.toLowerCase().includes(searchLower))
        const matchingSchedules = matchingPractitioners.map(p => resolveDaySchedule(date, p, schedules))

        return {
            mode: 'search',
            schedules: matchingSchedules,
            selectedPractitioners,
        }
    }

    const schedulesWithAssignedSurgery = schedules.filter(s => isAssignedSurgeryInThisLocation(s, locationId))

    const availableSchedulesSelectedSpecialties = schedulesWithAssignedSurgery.filter(s =>
        isSpecialtiesSelectionEnabled
            ? allocatedSpecialityUuids.has(s.practitioner.speciality_uuid)
            : s.practitioner.departments.some(dept => dept.id === departmentKey || departmentKey === 'all')
    )

    const availableSchedulesOtherSpecialties = schedulesWithAssignedSurgery.filter(schedule =>
        isSpecialtiesSelectionEnabled
            ? !allocatedSpecialityUuids.has(schedule.practitioner.speciality_uuid)
            : departmentKey !== 'all' && schedule.practitioner.departments.some(department => department.id !== departmentKey)
    )

    const schedulesAtOtherLocations = schedules.filter(s => s.scheduleLocations.some(loc => loc.location_id !== locationId))

    const listedIds = new Set(
        [...availableSchedulesSelectedSpecialties, ...availableSchedulesOtherSpecialties, ...schedulesAtOtherLocations].map(s => s.practitioner.id)
    )
    const otherPractitionerSchedules = allPractitioners.filter(p => !listedIds.has(p.id)).map(p => resolveDaySchedule(date, p, schedules))

    return {
        mode: 'browse',
        availableSchedulesSelectedSpecialties,
        availableSchedulesOtherSpecialties,
        schedulesAtOtherLocations,
        otherPractitionerSchedules,
        selectedPractitioners,
    }
}
